import React from 'react';
import { Phone, X, Bot } from 'lucide-react';

interface CallConfirmationOverlayProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  contactName?: string;
  callType?: 'direct' | 'ai-agent';
}

const CallConfirmationOverlay = ({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  contactName,
  callType = 'direct'
}: CallConfirmationOverlayProps) => {
  const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  }, [onCancel]);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const isAIAgent = callType === 'ai-agent';
  const confirmLabel = isAIAgent ? 'Yes, AI Agent call' : 'Yes, make call';
  const questionText = isAIAgent
    ? `AI Agent call to ${contactName}?`
    : `Do you want to call ${contactName}?`;

  return (
    <div 
      className="absolute inset-0 bg-destructive/70 dark:bg-destructive/80 flex flex-col items-center justify-center z-50 rounded-md animate-fade-in backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      {/* Two column layout: Text and Buttons */}
      <div className="flex items-center justify-between w-full px-8">
        {/* Left column: Text */}
        <div className="flex-1 flex items-center gap-2">
          {isAIAgent && <Bot className="h-5 w-5 text-black/90 dark:text-white flex-shrink-0" />}
          <h3 className="text-xl font-semibold text-black/90 dark:text-white">
            {questionText}
          </h3>
        </div>
        
        {/* Right column: Action buttons */}
        <div className="flex items-center gap-4">
          {/* YES Button */}
          <div
            onClick={onConfirm}
            className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded-full h-8 w-8 flex items-center justify-center cursor-pointer transition-colors"
            aria-label={confirmLabel}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onConfirm()}
          >
            {isAIAgent ? <Bot className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
          </div>
          
          {/* NO Button - X Icon */}
          <div
            onClick={onCancel}
            className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-full h-8 w-8 flex items-center justify-center cursor-pointer transition-colors"
            aria-label="No, cancel call"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onCancel()}
          >
            <X className="h-3 w-3" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallConfirmationOverlay;